import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  NoteCreateRequest,
  NoteItem,
  NoteUpdateRequest,
} from '@crm/shared';
import { api } from '../../lib/api';

export const notesKeys = {
  all: ['notes'] as const,
  forAppointment: (appointmentId: number) =>
    ['notes', 'appointment', appointmentId] as const,
};

export function useAppointmentNotes(appointmentId: number | null) {
  return useQuery({
    queryKey:
      appointmentId !== null
        ? notesKeys.forAppointment(appointmentId)
        : ['notes', 'appointment', 'noop'],
    enabled: appointmentId !== null,
    queryFn: async () => {
      const res = await api.get<NoteItem[]>(`/appointments/${appointmentId}/notes`);
      return res.data;
    },
  });
}

export function useNoteCreate(appointmentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NoteCreateRequest) => {
      const res = await api.post<NoteItem>(
        `/appointments/${appointmentId}/notes`,
        input,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notesKeys.forAppointment(appointmentId) });
      // Historia klienta korzysta z notatek — niech się odświeży.
      qc.invalidateQueries({ queryKey: ['clients', 'history'] });
    },
  });
}

export function useNoteUpdate(appointmentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: number; input: NoteUpdateRequest }) => {
      const res = await api.patch<NoteItem>(`/notes/${id}`, input);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notesKeys.forAppointment(appointmentId) });
      qc.invalidateQueries({ queryKey: ['clients', 'history'] });
    },
  });
}

export function useNoteDelete(appointmentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/notes/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notesKeys.forAppointment(appointmentId) });
      qc.invalidateQueries({ queryKey: ['clients', 'history'] });
    },
  });
}
